import { distanceMeters } from "./address";
import type { MapJob } from "./api-types";
import type { MapFilters } from "./settings";

export const METERS_PER_MILE = 1609.344;

/** The jobs a set of Map filters keeps (hail: the reports, when "only jobs near hail" is on). */
export function filterMapJobs(jobs: MapJob[], f: MapFilters, hail: { lat: number; lon: number }[] = []): MapJob[] {
  const from = f.from ? new Date(`${f.from}T00:00:00`).getTime() : null;
  const to = f.to ? new Date(`${f.to}T00:00:00`).getTime() + 86_400_000 : null;
  const words = f.search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const carrier = f.carrier.trim().toLowerCase();
  const near = f.hail.on && f.hail.nearOnly ? f.hail.miles * METERS_PER_MILE : null;
  return jobs.filter((j) => {
    if (!f.status.includes(j.stageKind)) return false;
    if (f.workflowIds.length && !f.workflowIds.includes(j.workflowId)) return false;
    if (f.stageIds.length && !f.stageIds.includes(j.stageId)) return false;
    if (f.repIds.length && !f.repIds.includes(j.salesRep?.id ?? "none") && !(j.otherRepIds ?? []).some((r) => f.repIds.includes(r))) return false;
    if (from !== null || to !== null) {
      const iso = f.dateField === "sold" ? j.soldAt : f.dateField === "changed" ? j.stageChangedAt : j.createdAt;
      if (!iso) return false;
      const t = new Date(iso).getTime();
      if ((from !== null && t < from) || (to !== null && t >= to)) return false;
    }
    if (carrier && !j.carrier.toLowerCase().includes(carrier)) return false;
    if (f.minValue !== null && (j.valueCents === null || j.valueCents < f.minValue * 100)) return false;
    if (f.maxValue !== null && (j.valueCents === null || j.valueCents > f.maxValue * 100)) return false;
    if (words.length) {
      const text = `#${j.number} ${j.number} ${j.customerName} ${j.address} ${j.title}`.toLowerCase();
      if (!words.every((w) => text.includes(w))) return false;
    }
    if (near !== null) {
      if (j.lat === null || j.lon === null) return false;
      const at = { lat: j.lat, lon: j.lon };
      if (!hail.some((h) => distanceMeters(at, h) <= near)) return false;
    }
    return true;
  });
}

/** Hail size colors: under 1", to 1.75" (golf ball), to 2.5", and bigger. */
export function hailColor(inches: number): string {
  return inches >= 2.5 ? "#7C3AED" : inches >= 1.75 ? "#DC2626" : inches >= 1 ? "#EA580C" : "#CA8A04";
}

/** "Quarter" and friends, the way spotters name hail. */
export function hailName(inches: number): string {
  const names: [number, string][] = [[4.5, "Softball"], [4, "Grapefruit"], [2.75, "Baseball"], [2.5, "Tennis ball"], [1.75, "Golf ball"], [1.5, "Ping pong ball"], [1.25, "Half dollar"], [1, "Quarter"], [0.88, "Nickel"], [0.75, "Penny"]];
  return names.find(([n]) => inches >= n)?.[1] ?? "Pea";
}
